/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import {
  Button,
  Form,
  Icon,
  Modal,
  Segment,
  Table,
} from '@mairistem/composants';

import { useProfessions } from '@/hooks';
import type { Profession } from '@/hooks';

interface ModalProfessionsProps {
  open: boolean;
  onClose: () => void;
  /** Appelée avec le LIBELLÉ retenu : c'est lui que l'acte stocke. */
  onSelect: (libelle: string) => void;
}

/**
 * Liste des professions du référentiel, **uniquement sélectionnable**.
 *
 * Pas de création ni de suppression, contrairement à la modale du recensement
 * citoyen qui administre un autre référentiel : celui-ci s'enrichit seul via
 * `AlimenteReferentielProfession`. Un écran de création ouvrirait un second
 * chemin d'alimentation, avec le risque de doublons de casse.
 */
const ModalProfessions = (props: ModalProfessionsProps) => {
  const { open, onClose, onSelect } = props;

  const [recherche, setRecherche] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setRecherche('');
    }
  }, [open]);

  // Le référentiel n'est interrogé que modale ouverte.
  const { professions, loading } = useProfessions(recherche, open);

  const choisir = React.useCallback(
    (profession: Profession) => {
      onSelect(profession.libelle);
      onClose();
    },
    [onSelect, onClose],
  );

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Choisir une profession" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Form.Input
            fluid
            icon="search"
            name="recherche"
            placeholder="Rechercher une profession..."
            data-testid="professions-recherche"
            value={recherche}
            onChange={(
              _event: React.SyntheticEvent,
              { value }: { value: string },
            ) => setRecherche(value)}
          />
        </Form>
        {/* Hauteur bornée : le référentiel compte des centaines d'entrées. */}
        <div style={{ maxHeight: '22em', overflowY: 'auto', marginTop: '1em' }}>
          {professions.length === 0 ? (
            <Segment basic textAlign="center" data-testid="professions-vide">
              {loading
                ? 'Chargement...'
                : 'Aucune profession ne correspond à cette recherche'}
            </Segment>
          ) : (
            <Table selectable compact basic="very">
              <Table.Body>
                {professions.map((profession) => (
                  <Table.Row
                    key={profession.identifiant ?? profession.libelle}
                    style={{ cursor: 'pointer' }}
                    data-testid={`profession-${profession.libelle}`}
                    onClick={() => choisir(profession)}
                  >
                    <Table.Cell>{profession.libelle}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalProfessions;
